/**
 * Suppliers Management Page
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, InputAdornment,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Edit, Block, Search } from '@mui/icons-material';
import api from '../services/api';
import PageHeader from '../components/layout/PageHeader';
import PageFab from '../components/layout/PageFab';
import { ListToolbar, RowAction, StatusChip } from '../components/common/ListParts';
import { API_ENDPOINTS } from '../config/api';
import { Supplier, PaginatedResponse } from '../types';
import toast from 'react-hot-toast';

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const EMPTY_FORM = { supplier_name: '', contact_person: '', contact_no: '', email: '', address: '', gst_no: '', status: 'active' };

const apiErrorMessage = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d: any) => String(d.msg || '').replace(/^Value error, /, '')).join('; ');
  }
  return fallback;
};

const Suppliers: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState<Supplier | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const gstValid = !formData.gst_no || GSTIN_REGEX.test(formData.gst_no);

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<PaginatedResponse<Supplier>>(API_ENDPOINTS.SUPPLIERS, {
        params: { page: page + 1, page_size: pageSize, search }
      });
      setSuppliers(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch suppliers');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const handleOpenDialog = (supplier?: Supplier) => {
    if (supplier) {
      setEditSupplier(supplier);
      setFormData({
        supplier_name: supplier.supplier_name, contact_person: supplier.contact_person || '',
        contact_no: supplier.contact_no || '', email: supplier.email || '', address: supplier.address || '',
        gst_no: supplier.gst_no || '', status: supplier.status
      });
    } else {
      setEditSupplier(null);
      setFormData(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.supplier_name.trim()) { toast.error('Supplier name is required'); return; }
    if (!gstValid) { toast.error('Invalid GSTIN'); return; }
    try {
      const data = { ...formData, supplier_name: formData.supplier_name.trim() };
      if (editSupplier) {
        await api.put(`${API_ENDPOINTS.SUPPLIERS}/${editSupplier.id}`, data);
        toast.success('Supplier updated');
      } else {
        await api.post(API_ENDPOINTS.SUPPLIERS, data);
        toast.success('Supplier created');
      }
      setDialogOpen(false);
      fetchSuppliers();
    } catch (error: any) {
      toast.error(apiErrorMessage(error, 'Failed to save supplier'));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Deactivate this supplier? Purchase history is kept.')) return;
    try {
      await api.delete(`${API_ENDPOINTS.SUPPLIERS}/${id}`);
      toast.success('Supplier deactivated');
      fetchSuppliers();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to deactivate supplier'));
    }
  };

  const columns: GridColDef[] = [
    { field: 'supplier_code', headerName: 'Code', width: 100 },
    { field: 'supplier_name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'contact_person', headerName: 'Contact', width: 130 },
    { field: 'contact_no', headerName: 'Phone', width: 130 },
    { field: 'gst_no', headerName: 'GSTIN', width: 170 },
    { field: 'email', headerName: 'Email', width: 180 },
    { field: 'status', headerName: 'Status', width: 100, renderCell: (params: GridRenderCellParams) => (
      <StatusChip status={params.value} />
    )},
    { field: 'actions', headerName: '', width: 100, sortable: false, align: 'right', renderCell: (params: GridRenderCellParams) => (
      <>
        <RowAction title="Edit" icon={<Edit fontSize="small" />} onClick={() => handleOpenDialog(params.row)} />
        <RowAction title="Deactivate" icon={<Block fontSize="small" />} color="error"
          disabled={params.row.status !== 'active'} onClick={() => handleDelete(params.row.id)} />
      </>
    )},
  ];

  return (
    <Box>
      <PageHeader title="Suppliers" />
      <Card>
        <ListToolbar>
          <TextField placeholder="Search suppliers..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} sx={{ flex: 1 }} />
        </ListToolbar>
        <DataGrid rows={suppliers} columns={columns} loading={loading} rowCount={total}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => { setPage(model.page); setPageSize(model.pageSize); }}
          pageSizeOptions={[10, 25, 50]} autoHeight disableRowSelectionOnClick />
      </Card>
      <PageFab label="Add Supplier" onClick={() => handleOpenDialog()} />
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editSupplier ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {editSupplier && <Grid size={12}><TextField fullWidth label="Supplier Code" value={editSupplier.supplier_code} disabled /></Grid>}
            <Grid size={12}><TextField fullWidth label="Supplier Name" required value={formData.supplier_name}
                onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth label="Contact Person" value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth label="Phone" value={formData.contact_no}
                onChange={(e) => setFormData({ ...formData, contact_no: e.target.value })} inputProps={{ maxLength: 20 }} /></Grid>
            <Grid size={12}><TextField fullWidth label="GSTIN (optional)" value={formData.gst_no}
                onChange={(e) => setFormData({ ...formData, gst_no: e.target.value.toUpperCase().trim() })}
                error={!gstValid} helperText={!gstValid ? 'Invalid GSTIN (15 characters, e.g. 27ABCDE1234F1Z5)' : ''}
                inputProps={{ maxLength: 15 }} /></Grid>
            <Grid size={12}><TextField fullWidth label="Email" type="email" value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></Grid>
            <Grid size={12}><TextField fullWidth label="Address" multiline rows={2} value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></Grid>
            <Grid size={12}><FormControl fullWidth><InputLabel>Status</InputLabel>
              <Select value={formData.status} label="Status" onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                <MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
              </Select></FormControl></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Suppliers;

